import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageShell } from "@/components/PageShell";
import { KeyGate, Candidate, ManualRow, INPUT, leadText } from "@/components/ManualConfirmParts";
import { ManualConfirmForm } from "@/components/ManualConfirmForm";

const KEY = "vigilo_admin_key";

export default function ManualConfirm() {
  const [key, setKey] = useState(sessionStorage.getItem(KEY) || "");
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [cands, setCands] = useState([]);
  const [pick, setPick] = useState(null);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0);

  const unlock = (k) => {
    setBusy(true);
    api.adminCheck(k).then(() => { sessionStorage.setItem(KEY, k); setKey(k); setOk(true); setErr(""); })
      .catch((e) => { sessionStorage.removeItem(KEY); setErr(e.message); }).finally(() => setBusy(false));
  };
  useEffect(() => { if (key) unlock(key); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ok) return undefined;
    const t = setTimeout(() => api.manualCandidates(key, q).then(setCands).catch((e) => toast.error(e.message)), 250);
    return () => clearTimeout(t);
  }, [ok, q, key, tick]);
  const loadList = () => api.manualList(key).then(setList).catch(() => {});
  useEffect(() => { if (ok) loadList(); }, [ok]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = (body) => {
    setBusy(true);
    api.manualConfirm(key, pick.uid, body).then((r) => {
      toast.success(`Logged · lead ${leadText(r.lead_hours)} · ${r.late ? "Confirmed (late)" : "Confirmed (hit)"}`);
      setPick(null); setCands((c) => c.filter((x) => x.uid !== r.uid)); loadList();
    }).catch((e) => toast.error(e.message)).finally(() => setBusy(false));
  };
  const revert = (r) => api.manualRevert(key, r.uid).then((x) => { toast.success(`Reverted · restored as ${x.restored}`); loadList(); setTick((t) => t + 1); })
    .catch((e) => toast.error(e.message));

  return (
    <PageShell testid="manual-confirm-page" icon={ShieldCheck} iconClass="text-amber-300" title="Manual confirmation"
      intro="Log an official agency warning or report that Vigilo's feeds did not carry. It uses the agency's own issue time for lead time, follows the same window rules as automatic scoring, and is tagged 'Manually verified' on the public ledger.">
      {!ok ? <KeyGate onUnlock={unlock} error={err} busy={busy} /> : (
        <div className="flex flex-col gap-10">
          <div className="grid md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-6">
            <div className="flex flex-col gap-2">
              <input className={INPUT} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search region, e.g. Sierra Madre" data-testid="manual-search-input" />
              <div className="border border-white/10 bg-atlas-surface/70 max-h-[460px] overflow-y-auto" data-testid="manual-candidates">
                {cands.map((c) => <Candidate key={c.uid} c={c} active={pick?.uid === c.uid} onPick={setPick} />)}
                {cands.length === 0 && <p className="text-[11px] font-mono text-atlas-secondary/80 p-4" data-testid="manual-no-candidates">No open or unconfirmed calls match.</p>}
              </div>
            </div>
            <div>{pick ? <ManualConfirmForm key={pick.uid} c={pick} onSubmit={submit} busy={busy} />
              : <p className="text-[11px] font-mono text-atlas-secondary/85 border border-dashed border-white/15 p-6" data-testid="manual-pick-hint">Pick a call on the left.</p>}</div>
          </div>
          <div data-testid="manual-list">
            <h2 className="text-base md:text-lg font-display text-white mb-2">Manually verified hits</h2>
            {list.length === 0 ? <p className="text-[11px] font-mono text-atlas-secondary/80" data-testid="manual-list-empty">None yet.</p>
              : list.map((r) => <ManualRow key={r.uid} r={r} onRevert={revert} />)}
          </div>
        </div>
      )}
    </PageShell>
  );
}
